/**
 * Welche Aufteilung und Kostenstufe der Altersvorsorgedepot-Rechner zeigt — und
 * der erste Stand seiner Spanne, den der Server vorlegt.
 *
 * Seit 25.10.2026 steht die Spanne für die Eingaben aus der Adresse schon im
 * Server-HTML (`app/_seo/lokal.ts` → `avDepotVorlage`), statt „wird geladen …“.
 * Dafür müssen Server und Browser dieselbe Aufteilung wählen: Die Regel steht
 * deshalb hier, ohne React, und beide Seiten rufen sie — die Seite
 * (`seiten/AltersvorsorgedepotPage.tsx`) und der Server, der die Tabellen dazu
 * holt. Eine zweite Fassung der Wahl hiesse: Der Server legt die Tabellen einer
 * anderen Aufteilung vor, als die Seite zeigt.
 */
import type { components } from '../types/api.generated';

export type AvDepotAufteilung = components['schemas']['AvDepotAufteilung'];
export type AvDepotFaktoren = components['schemas']['AvDepotFaktorenResponse'];

/** Kostenstufe ohne Angabe in der Adresse (Basispunkte, `av_depot_faktoren.KOSTEN_BP`). */
export const KOSTEN_VORGABE_BP = 50;

/** Eine Zahl aus der Adresse — ganz, in den Grenzen, sonst die Vorgabe. */
export function zahlAus(params: URLSearchParams, name: string, min: number, max: number, vorgabe: number): number {
  const text = params.get(name);
  if (text === null || !/^\d+$/.test(text)) return vorgabe;
  const zahl = Number(text);
  return zahl >= min && zahl <= max ? zahl : vorgabe;
}

/**
 * Aufteilung und Kostenstufe der Adresse: die gewählte, sonst die erste der
 * Liste; die gewählte Kostenstufe, wenn es sie für diese Aufteilung gibt,
 * sonst ihre erste.
 */
export function avAuswahl(
  aufteilungen: AvDepotAufteilung[] | null,
  params: URLSearchParams,
): { aufteilung: AvDepotAufteilung | null; kosten: number | null } {
  const gewaehlt = params.get('aufteilung');
  const kostenBp = zahlAus(params, 'kosten', 0, 1000, KOSTEN_VORGABE_BP);
  const aufteilung = aufteilungen?.find((a) => a.kennung === gewaehlt) ?? aufteilungen?.[0] ?? null;
  const kosten = aufteilung?.kosten_bp.includes(kostenBp) ? kostenBp : (aufteilung?.kosten_bp[0] ?? null);
  return { aufteilung, kosten };
}

/** Schlüssel einer Tabelle: Aufteilung und Kostenstufe. */
export function faktorSchluessel(kennung: string, kostenBp: number): string {
  return `${kennung}:${kostenBp}`;
}

/** Was der Server vorlegt: die Liste und, wenn sie geladen werden konnten, die Tabellen der Wahl. */
export interface AvDepotVorlage {
  aufteilungen: AvDepotAufteilung[];
  faktoren: AvDepotFaktoren | null;
}

let vorgelegt: AvDepotVorlage | null = null;

/**
 * Vor dem ersten Rendern, auf Server und Browser gleich (`OeffentlicheSeite`).
 * Gerufen für JEDE Server-Seite, auch mit `null`: Der Server hält Modulzustand
 * über Anfragen hinweg — eine Vorlage der vorigen Anfrage (andere Adresse,
 * andere Aufteilung) stünde sonst im HTML, und der Browser sähe etwas anderes.
 */
export function legeAvDepotVor(vorlage: AvDepotVorlage | null): void {
  vorgelegt = vorlage;
}

export function vorgelegteAvDepotDaten(): AvDepotVorlage | null {
  return vorgelegt;
}
